import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  Snackbar,
  Alert,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FolderIcon from "@mui/icons-material/Folder";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { knowledgeBaseApis, knowledgeArticleApis } from "../../Data/axios";

const KnowledgeBase = () => {
  const navigate = useNavigate();

  // Data states
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Add Article Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addTitle, setAddTitle] = useState("");
  const [addDescription, setAddDescription] = useState("");
  const [addCategoryId, setAddCategoryId] = useState("");
  const [addTarget, setAddTarget] = useState("");
  const [addUserType, setAddUserType] = useState("");
  const [addFile, setAddFile] = useState(null);
  const [addFileName, setAddFileName] = useState("");

  // Edit Article Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editArticle, setEditArticle] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCategoryId, setEditCategoryId] = useState("");
  const [editTarget, setEditTarget] = useState("");
  const [editUserType, setEditUserType] = useState("");
  const [editFile, setEditFile] = useState(null);
  const [editFileName, setEditFileName] = useState("");

  // Category Modal states
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryName, setCategoryName] = useState("");

  // Filter states
  const [filterTitle, setFilterTitle] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterUserType, setFilterUserType] = useState("");

  // Alert state
  const [alert, setAlert] = useState({ open: false, message: "", severity: "info" });

  // Quill modules
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  const showAlert = (message, severity) => {
    setAlert({ open: true, message, severity });
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const data = await knowledgeArticleApis.getAll();
      if (Array.isArray(data)) {
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      showAlert("Failed to load categories", "error");
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Fetch articles
  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        title: filterTitle || undefined,
        status: filterStatus === "" ? undefined : filterStatus,
        user_type: filterUserType === "" ? undefined : filterUserType,
      };

      const response = await knowledgeBaseApis.getAll(params);

      if (response?.data?.records) {
        setArticles(response.data.records);
        setTotalRecords(response.data.total_records || 0);
      } else {
        setArticles([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching articles:", error);
      showAlert("Failed to load articles", "error");
      setArticles([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterTitle, filterStatus, filterUserType]);

  // Initial load
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch articles when filters or pagination change
  useEffect(() => {
    fetchArticles();
  }, [page, rowsPerPage, filterTitle, filterStatus, filterUserType, fetchArticles]);

  // Reset add form
  const resetAddForm = () => {
    setAddTitle("");
    setAddDescription("");
    setAddCategoryId("");
    setAddTarget("");
    setAddUserType("");
    setAddFile(null);
    setAddFileName("");
  };

  // Handle file upload
  const handleAddFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAddFile(file);
      setAddFileName(file.name);
    }
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditFile(file);
      setEditFileName(file.name);
    }
  };

  // Add article
  const handleAddArticle = async () => {
    if (!addTitle.trim()) {
      showAlert("Please enter article title", "warning");
      return;
    }
    if (!addDescription.trim()) {
      showAlert("Please enter description", "warning");
      return;
    }
    if (!addCategoryId) {
      showAlert("Please select category", "warning");
      return;
    }
    if (!addTarget) {
      showAlert("Please select target audience", "warning");
      return;
    }
    // Only require user type if target is "App User"
    if (addTarget === "3" && !addUserType) {
      showAlert("Please select user type", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("title", addTitle);
    formData.append("description", addDescription);
    formData.append("article_id", addCategoryId);
    formData.append("for", addTarget);
    // Only append user_type if target is "App User"
    if (addTarget === "3") {
      formData.append("user_type", addUserType);
    }
    if (addFile) {
      formData.append("file", addFile);
    }

    try {
      const response = await knowledgeBaseApis.add(formData);
      if (response === "1602") {
        showAlert("Article added successfully", "success");
        setAddModalOpen(false);
        resetAddForm();
        fetchArticles();
      } else {
        showAlert("Failed to add article", "error");
      }
    } catch (error) {
      console.error("Error adding article:", error);
      showAlert("Failed to add article", "error");
    }
  };

  // Edit article
  const handleEditArticle = async () => {
    if (!editArticle) return;

    const formData = new FormData();
    formData.append("title", editTitle);
    formData.append("description", editDescription);
    formData.append("article_id", editCategoryId);
    formData.append("for", editTarget);
    // Only append user_type if target is "App User"
    if (editTarget === "3") {
      formData.append("user_type", editUserType);
    }
    if (editFile) {
      formData.append("file", editFile);
    }

    try {
      const response = await knowledgeBaseApis.updateById(editArticle.id, formData);
      if (response === "1602") {
        showAlert("Article updated successfully", "success");
        setEditModalOpen(false);
        fetchArticles();
      } else {
        showAlert("Failed to update article", "error");
      }
    } catch (error) {
      console.error("Error updating article:", error);
      showAlert("Failed to update article", "error");
    }
  };

  // Delete article
  const handleDeleteArticle = async (id) => {
    if (window.confirm("Are you sure you want to delete this article?")) {
      try {
        const response = await knowledgeBaseApis.deleteById(id);
        showAlert("Article deleted successfully", "success");
        fetchArticles();
      } catch (error) {
        console.error("Error deleting article:", error);
        showAlert("Failed to delete article", "error");
      }
    }
  };

  // Open edit modal
  const openEditModal = async (article) => {
    try {
      const articleData = await knowledgeBaseApis.getById(article.id);
      setEditArticle(articleData);
      setEditTitle(articleData.title || "");
      setEditDescription(articleData.description || "");
      setEditCategoryId(articleData.article_id || "");
      setEditTarget(articleData.for?.toString() || "");
      setEditUserType(articleData.user_type?.toString() || "");
      setEditFile(null);
      setEditFileName(articleData.file?.split('/').pop() || "");
      setEditModalOpen(true);
    } catch (error) {
      console.error("Error loading article details:", error);
      showAlert("Failed to load article details", "error");
    }
  };

  // View article
  const viewArticle = (article) => {
    navigate(`/knowledge/${article.id}`, { state: article });
  };

  // Add category
  const handleAddCategory = async () => {
    if (!categoryName.trim()) {
      showAlert("Please enter category name", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("title", categoryName);

    try {
      const response = await knowledgeArticleApis.add(formData);
      console.log("Category add response:", response);
      // Check for various success indicators
      if (response === "1602" || response === true || response?.success === true || response?.status === "success") {
        showAlert("Category added successfully", "success");
        setCategoryModalOpen(false);
        setCategoryName("");
        fetchCategories();
      } else {
        showAlert("Failed to add category", "error");
      }
    } catch (error) {
      console.error("Error adding category:", error);
      showAlert("Failed to add category", "error");
    }
  };

  // Delete category
  const handleDeleteCategory = async (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        await knowledgeArticleApis.deleteById(id);
        showAlert("Category deleted successfully", "success");
        fetchCategories();
      } catch (error) {
        console.error("Error deleting category:", error);
        showAlert("Failed to delete category", "error");
      }
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#1976d2" }}>
            📚 Knowledge Base
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<FolderIcon />}
              onClick={() => setCategoryModalOpen(true)}
              sx={{
                backgroundColor: "#f44336",
                "&:hover": { backgroundColor: "#d32f2f" },
              }}
            >
              Manage Categories
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetAddForm();
                setAddModalOpen(true);
              }}
              sx={{
                backgroundColor: "#4caf50",
                "&:hover": { backgroundColor: "#388e3c" },
              }}
            >
              Add Article
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            placeholder="Search by title..."
            value={filterTitle}
            onChange={(e) => setFilterTitle(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "black" }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250, color: "black" }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: "black" }}>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
              sx={{ color: "black" }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="0">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ color: "black" }}>User Type</InputLabel>
            <Select
              value={filterUserType}
              label="User Type"
              onChange={(e) => setFilterUserType(e.target.value)}
              sx={{ color: "black" }}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="1">Driver</MenuItem>
              <MenuItem value="2">Owner</MenuItem>
              <MenuItem value="3">Dhaba</MenuItem>
              <MenuItem value="4">Mechanic</MenuItem>
              <MenuItem value="5">Crane Owner</MenuItem>
              <MenuItem value="6">Crane Operator</MenuItem>
            </Select>
          </FormControl>

          {(filterTitle || filterStatus || filterUserType) && (
            <Button
              variant="outlined"
              onClick={() => {
                setFilterTitle("");
                setFilterStatus("");
                setFilterUserType("");
              }}
              sx={{ height: 36, color: "black" }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {/* Categories Display */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: "white" }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "black" }}>
          📁 Categories
        </Typography>
        {categoriesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : categories.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, color: "gray" }}>
            No categories found
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {categories.map((category) => (
              <Chip
                key={category.id}
                label={category.title}
                onDelete={() => handleDeleteCategory(category.id)}
                deleteIcon={<DeleteIcon />}
                sx={{
                  backgroundColor: "#e3f2fd",
                  color: "#1565c0",
                  "&:hover": { backgroundColor: "#bbdefb" },
                }}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Articles Table */}
      <Paper sx={{ borderRadius: 2, backgroundColor: "white", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: "black" }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "black" }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "black" }}>Target</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "black" }}>User Type</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "black" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "black" }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "black" }}>File</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "black" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : articles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "gray" }}>
                    No articles found
                  </TableCell>
                </TableRow>
              ) : (
                articles.map((article) => (
                  <TableRow key={article.id} hover sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}>
                    <TableCell sx={{ fontWeight: 500, color: "black" }}>{article.title}</TableCell>
                    <TableCell sx={{ color: "black" }}>{article.article || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={article.for === "1" ? "Client" : article.for === "2" ? "Employee" : "App User"}
                        size="small"
                        sx={{
                          backgroundColor:
                            article.for === "1"
                              ? "#ffcdd2"
                              : article.for === "2"
                              ? "#d1ecf1"
                              : "#d5f5e3",
                          color: "black",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          article.user_type === "1"
                            ? "Driver"
                            : article.user_type === "2"
                            ? "Owner"
                            : article.user_type === "3"
                            ? "Dhaba"
                            : article.user_type === "4"
                            ? "Mechanic"
                            : article.user_type === "5"
                            ? "Crane Owner"
                            : "Crane Operator"
                        }
                        size="small"
                        sx={{ backgroundColor: "#c8e6c9", color: "black" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={article.status === "1" ? "Active" : "Inactive"}
                        size="small"
                        sx={{
                          backgroundColor: article.status === "1" ? "#c8e6c9" : "#ffcdd2",
                          color: article.status === "1" ? "#1b5e20" : "#7f8c8d",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "black" }}>{article.creator_name || "-"}</TableCell>
                    <TableCell>
                      {article.file ? (
                        <Tooltip title={article.file.split('/').pop()}>
                          <Chip label="📎 File" size="small" sx={{ backgroundColor: "#e1f5fe", color: "black" }} />
                        </Tooltip>
                      ) : (
                        <span style={{ color: "black" }}>-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => viewArticle(article)}
                            sx={{ color: "#2196f3" }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEditModal(article)}
                            sx={{ color: "#ff9800" }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteArticle(article.id)}
                            sx={{ color: "#f44336" }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ borderTop: "1px solid #e0e0e0" }}
        />
      </Paper>

      {/* Add Article Modal */}
      <Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#1976d2", color: "white" }}>
          Add New Article
          <IconButton
            onClick={() => setAddModalOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto", backgroundColor: "white", color: "black" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Article Title"
              fullWidth
              value={addTitle}
              onChange={(e) => setAddTitle(e.target.value)}
              required
              sx={{
                "& .MuiInputLabel-root": { color: "black" },
                "& .MuiOutlinedInput-root": {
                  color: "black",
                  "& fieldset": { borderColor: "black" },
                },
              }}
            />

            <FormControl fullWidth>
              <InputLabel sx={{ color: "black" }}>Category *</InputLabel>
              <Select
                value={addCategoryId}
                label="Category *"
                onChange={(e) => setAddCategoryId(e.target.value)}
                sx={{ color: "black" }}
              >
                <MenuItem value="">Select Category</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel sx={{ color: "black" }}>Target Audience *</InputLabel>
              <Select
                value={addTarget}
                label="Target Audience *"
                onChange={(e) => {
                  setAddTarget(e.target.value);
                  // Reset user type if target is not "App User"
                  if (e.target.value !== "3") {
                    setAddUserType("");
                  }
                }}
                sx={{ color: "black" }}
              >
                <MenuItem value="">Select Target</MenuItem>
                <MenuItem value="1">Client</MenuItem>
                <MenuItem value="2">Employee</MenuItem>
                <MenuItem value="3">App User</MenuItem>
              </Select>
            </FormControl>

            {addTarget === "3" && (
              <FormControl fullWidth>
                <InputLabel sx={{ color: "black" }}>User Type *</InputLabel>
                <Select
                  value={addUserType}
                  label="User Type *"
                  onChange={(e) => setAddUserType(e.target.value)}
                  sx={{ color: "black" }}
                >
                  <MenuItem value="">Select Type</MenuItem>
                  <MenuItem value="1">Driver</MenuItem>
                  <MenuItem value="2">Owner</MenuItem>
                  <MenuItem value="3">Dhaba</MenuItem>
                  <MenuItem value="4">Mechanic</MenuItem>
                  <MenuItem value="5">Crane Owner</MenuItem>
                  <MenuItem value="6">Crane Operator</MenuItem>
                </Select>
              </FormControl>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "black" }}>
                Description *
              </Typography>
              <ReactQuill
                theme="snow"
                value={addDescription}
                onChange={setAddDescription}
                modules={quillModules}
                style={{ height: 200, color: 'black' }}
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "black" }}>
                File (Optional)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                fullWidth
              >
                {addFileName || "Choose File"}
                <input type="file" hidden onChange={handleAddFileChange} />
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: "white", color: "black" }}>
          <Button onClick={() => setAddModalOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddArticle} variant="contained" sx={{ backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#388e3c" } }}>
            Save Article
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Article Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#ff9800", color: "white" }}>
          Edit Article
          <IconButton
            onClick={() => setEditModalOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto", backgroundColor: "white", color: "black" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Article Title"
              fullWidth
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              required
              sx={{
                "& .MuiInputLabel-root": { color: "black" },
                "& .MuiOutlinedInput-root": {
                  color: "black",
                  "& fieldset": { borderColor: "black" },
                },
              }}
            />

            <FormControl fullWidth>
              <InputLabel sx={{ color: "black" }}>Category *</InputLabel>
              <Select
                value={editCategoryId}
                label="Category *"
                onChange={(e) => setEditCategoryId(e.target.value)}
                sx={{ color: "black" }}
              >
                <MenuItem value="">Select Category</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel sx={{ color: "black" }}>Target Audience *</InputLabel>
              <Select
                value={editTarget}
                label="Target Audience *"
                onChange={(e) => {
                  setEditTarget(e.target.value);
                  // Reset user type if target is not "App User"
                  if (e.target.value !== "3") {
                    setEditUserType("");
                  }
                }}
                sx={{ color: "black" }}
              >
                <MenuItem value="">Select Target</MenuItem>
                <MenuItem value="1">Client</MenuItem>
                <MenuItem value="2">Employee</MenuItem>
                <MenuItem value="3">App User</MenuItem>
              </Select>
            </FormControl>

            {editTarget === "3" && (
              <FormControl fullWidth>
                <InputLabel sx={{ color: "black" }}>User Type *</InputLabel>
                <Select
                  value={editUserType}
                  label="User Type *"
                  onChange={(e) => setEditUserType(e.target.value)}
                  sx={{ color: "black" }}
                >
                  <MenuItem value="">Select Type</MenuItem>
                  <MenuItem value="1">Driver</MenuItem>
                  <MenuItem value="2">Owner</MenuItem>
                  <MenuItem value="3">Dhaba</MenuItem>
                  <MenuItem value="4">Mechanic</MenuItem>
                  <MenuItem value="5">Crane Owner</MenuItem>
                  <MenuItem value="6">Crane Operator</MenuItem>
                </Select>
              </FormControl>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "black" }}>
                Description *
              </Typography>
              <ReactQuill
                theme="snow"
                value={editDescription}
                onChange={setEditDescription}
                modules={quillModules}
                style={{ height: 200, color: 'black' }}
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "black" }}>
                File (Optional - Leave empty to keep existing)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                fullWidth
              >
                {editFileName || "Choose New File"}
                <input type="file" hidden onChange={handleEditFileChange} />
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: "white", color: "black" }}>
          <Button onClick={() => setEditModalOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleEditArticle} variant="contained" sx={{ backgroundColor: "#ff9800", "&:hover": { backgroundColor: "#f57c00" } }}>
            Update Article
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Category Modal */}
      <Dialog open={categoryModalOpen} onClose={() => setCategoryModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#f44336", color: "white" }}>
          Add Category
          <IconButton
            onClick={() => setCategoryModalOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Category Name"
            fullWidth
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: "white", color: "black" }}>
          <Button onClick={() => setCategoryModalOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddCategory} variant="contained" sx={{ backgroundColor: "#f44336", "&:hover": { backgroundColor: "#d32f2f" } }}>
            Save Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
          {alert.message}
        </Alert>
      </Snackbar>

      <style>{`
        .ql-editor {
          color: black !important;
        }
        .ql-snow .ql-editor {
          color: black !important;
        }
      `}</style>
    </Box>
  );
};

export default KnowledgeBase;
